<script>
export default {
  name: 'OrderFavorite'
}
</script>

<script setup>
import { ref, reactive, onMounted } from 'vue';

import OrderNavigation from '@parts/OrderNavigation.vue';
import axios from 'axios';
import DeleteFavoriteConfirmModal from './modal/DeleteFavoriteConfirmModal.vue';
import DeleteFavoriteCompleteModal from './modal/DeleteFavoriteCompleteModal.vue';
import { apiError } from '@functions/apiError.js';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import { modalUtil } from '@functions/modalUtil.js';
import { url } from '@/config/urls';
import router from '@/router/index.js';

// 外部定義メソッド
const { showModal, switchFromCurrentModal } = modalUtil();

// 画面ID
const screenId = ref("OrderFavorite");

// エラーメッセージの状態管理
const errorMessages = ref([]);

// Prop定義
const props = defineProps({
  userDataUrl: {
    type: String,
    default: url.api.userData
  },
  orderFavoriteDetailPageUrl: {
    type: String,
    default: url.page.orderFavoriteDetail
  },
  orderFavoriteListApiUrl: {
    type: String,
    default: url.api.orderFavorite.list
  },
  orderFavoriteAddApiUrl: {
    type: String,
    default: url.api.orderFavorite.add
  },
  orderFavoriteUpdateApiUrl: {
    type: String,
    default: url.api.orderFavorite.update
  },
  orderFavoriteDeleteApiUrl: {
    type: String,
    default: url.api.orderFavorite.delete
  },
  orderCartCountApiUrl: {
    type: String,
    default: url.api.orderCart.count
  },
  goodsTypesApiUrl: {
    type: String,
    default: url.api.goodsTypes
  },
  errorUrl: {
    type: String,
    default: url.error
  },
  loadingIcon: {
    type: String,
    default: url.static.loadingIcon
  }
});

// ユーザー情報
const user = reactive({
  isPermittedOrder: null  // null: 未取得, true: 発注, false: 在庫問合せ
});

// ユーザー情報取得APIの呼び出し
const getUserInfo = async () => {
  try {
    const response = await axios.get(props.userDataUrl);
    user.isPermittedOrder = response.data.response.isPermittedOrder;
  } catch (error) {
    apiError(error, errorMessages);
  }
}

// ページネーション
const pagingParameter = ref({
  pageSize: 50,
  pageNum: 1
});
const totalPage = ref(1);

// 商品分類マスタデータ
const goodsTypes = ref([]);

// お気に入りグループ
const goodsFavorites = ref([]);
const hasFavoriteSlot = ref(false);

// 検索結果
const goodsList = ref([]);
const totalNum = ref(0);

// ローディングフラグ
const isLoading = ref(false);

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
  isLoading.value = loading;
}

// 新規お気に入りグループ名
const newGoodsFavName = ref('');

// 編集お気に入りグループ名
const editGoodsFavName = ref('');

// カート商品 品目数
const cartGoodsTotalRecord = ref(0);
// カート商品集計
const countCartGoods = () => {
  axios.get(props.orderCartCountApiUrl)
    .then((response) => {
      cartGoodsTotalRecord.value = parseInt(response.data.response.totalNum);
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// お気に入りマスタデータ取得処理
const getGoodsFavorites = () => {
  axios.get(props.orderFavoriteListApiUrl)
    .then((response) => {
      goodsFavorites.value = response.data.response.goodsFavorites.map(favorite => ({
        ...favorite,
        mode: 'view'
      }));
      hasFavoriteSlot.value = response.data.response.hasFavoriteSlot;
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

const onInputGoodsFavName = () => {
      // 20文字までの入力制限
      const chars = Array.from(newGoodsFavName.value);
      if (chars.length > 20) {
        newGoodsFavName.value = chars.slice(0, 20).join('');
      }
  }

// お気に入り詳細ページに遷移
const goToFavoriteDetail = (id) => {
  router.push({
    path: props.orderFavoriteDetailPageUrl
    , query: { id }
  });
}

// 表示モードを変更（view:閲覧, edit:編集）
const switchMode = (favoriteId, mode) => {
  // 編集モードに切り替える場合、他のすべてを閲覧モードに戻す
  if(mode === 'edit') {
    goodsFavorites.value.forEach(f => {
      if(f.id !== favoriteId) {
        f.mode = 'view'
      }
    })
  }
  const favorite = goodsFavorites.value.find(f => f.id === favoriteId);
  if (favorite) {
    favorite.mode = mode;
    if (mode === 'view') {
      editGoodsFavName.value = '';
    } else if (mode === 'edit') {
      editGoodsFavName.value = favorite.name;
    }
  }
}

// お気に入りグループ追加
const addFavorite = () => {
  const param = {
    goodsFavName: newGoodsFavName.value
  };
  handleUpdateLoading(true);
  axios.post(props.orderFavoriteAddApiUrl, param)
    .then((response) => {
      newGoodsFavName.value = '';
    })
    .catch((error) => {
      apiError(error, errorMessages);
    })
    .finally(() => {
      getGoodsFavorites();
      handleUpdateLoading(false);
    });
}

// お気に入りグループ名更新
const updateFavorite = (id) => {
  const param = {
    goodsFavId: id,
    goodsFavName: editGoodsFavName.value
  };
  handleUpdateLoading(true);
  axios.post(props.orderFavoriteUpdateApiUrl, param)
    .then((response) => {
      editGoodsFavName.value = '';
    })
    .catch((error) => {
      apiError(error, errorMessages);
    })
    .finally(() => {
      getGoodsFavorites();
      handleUpdateLoading(false);
    });
}

// 削除グループ情報
const deleteGoodsFavId = ref(0);
const deleteGoodsFavName = ref('');

// 削除確認
const confirmDelete = (id, name) => {
  deleteGoodsFavId.value = id;
  deleteGoodsFavName.value = name;
  showModal('deleteFavoriteConfirmModal');
}

// お気に入りグループ削除
const deleteFavorite = (id) => {
  const param = {
    goodsFavId: id
  };
  handleUpdateLoading(true);
  axios.post(props.orderFavoriteDeleteApiUrl, param)
    .then((response) => {
      // do nothing
    })
    .catch((error) => {
      apiError(error, errorMessages);
    })
    .finally(() => {
      getGoodsFavorites();
      handleUpdateLoading(false);
      switchFromCurrentModal('deleteFavoriteCompleteModal');
    });
}

// 初期処理
onMounted(async () => {
  await getUserInfo();
  if (!user.isPermittedOrder) {
    return;
  }

  countCartGoods();
  getGoodsFavorites();
});
</script>

<template>

  <div id="contents1" class="bg1" v-if="user.isPermittedOrder">
    <OrderNavigation
      :screenId = "screenId"
      :cartGoodsTotalRecord = cartGoodsTotalRecord
    ></OrderNavigation>

    <h2 class="com_title1">お気に入り一覧</h2>
    <div class="com_inner4 clearfix">
      <div v-for="fav in goodsFavorites" :key="fav.id">
        <div v-if="fav.mode === 'view'" @click="goToFavoriteDetail(fav.id)" class="com_favorite_group1">
          <div class="name1">
            {{ fav.name }}
            <a @click.stop="switchMode(fav.id, 'edit')"><img src="/front/assets/common/img/icon_edit2.png" alt="" class="icon_edit2"></a>
          </div>
          <div class="txt1">登録数：{{ fav.itemCount }}</div>
          <a class="delete1" @click.stop="confirmDelete(fav.id, fav.name)">グループを削除<img src="/front/assets/common/img/icon_close1.png" alt="" class="icon_close1"></a>
        </div>
        <div v-else-if="fav.mode === 'edit'" class="com_favorite_group1">
          <div class="edit1">
            <input type="text" placeholder="お気に入り名を入力してください" v-model="editGoodsFavName" class="form-control">
            <button type="button" class="btn" @click.stop="updateFavorite(fav.id)" :disabled="editGoodsFavName.trim() === ''">保存</button>
          </div>
          <div class="txt1">登録数：{{ fav.itemCount }}</div>
        </div>
      </div>
      <div v-if="hasFavoriteSlot">
        <div class="com_favorite_group1">
          <div class="edit1">
            <input type="text" v-model="newGoodsFavName" placeholder="お気に入り名を入力してください" class="form-control" @input="onInputGoodsFavName">
            <button type="button" class="btn" @click.stop="addFavorite()" :disabled="newGoodsFavName.trim() === ''">追加</button>
          </div>
          <div class="txt1">登録数：0</div>
        </div>
      </div>
    </div>
  </div>

  <!-- 削除確認モーダル -->
  <DeleteFavoriteConfirmModal
    :goodsFavId="deleteGoodsFavId"
    :goodsFavName="deleteGoodsFavName"
    @deleteFavorite="deleteFavorite"
  ></DeleteFavoriteConfirmModal>

  <!-- 削除完了モーダル -->
  <DeleteFavoriteCompleteModal></DeleteFavoriteCompleteModal>

  <ApiErrorModal
    :messages="errorMessages"
  ></ApiErrorModal>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>

</template>
